import {
  useGetAdverseEventTypes,
  useGetInterventionLevels,
  useGetMaintenanceTypes,
  useGetPoolRegulations,
  useGetReportTypes,
} from "../../../../shared/services/catalogService";
import type {
  NewFormData,
  PoolRegulationNewFormData,
  AdverseEventNewFormData,
} from "../../components/RegisterNewForm/RegisterNewForm.types";
import type { MaintenanceNewFormData } from "../../components/MaintenanceNewFormFields/MaintenanceNewFormFields.types";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../../../../routes/routes";
import {
  useCreateMaintenanceNew,
  useCreateFirstAidStationNew,
  useCreatePoolRegulationNew,
  useCreateAdverseEventNew,
  newQueryKeys,
  getDefaultNewsListParams,
} from "../../services/newService";
import { useQueryClient } from "@tanstack/react-query";
import type { New } from "../../../../api/client/types/new.types";
import type { PaginatedResponse } from "../../../../api/client/types/common.types";
import { useAuthStore } from "../../../../store";
import {
  isMaintenanceReportType,
  isFirstAidStationReportType,
  isPoolRegulationReportType,
  isAdverseEventReportType,
  isLlamadoDeAtencionInterventionLevel,
} from "../../utils/reportTypeConstants";
import { useGetSwimmerById } from "../../../swimmers/services/swimmerService";

export const useRegisterNewPage = () => {
  const { data: reportTypes } = useGetReportTypes();
  const { data: maintenanceTypes } = useGetMaintenanceTypes();
  const { data: poolRegulationTypes } = useGetPoolRegulations();
  const { data: interventionLevels } = useGetInterventionLevels();
  const { data: adverseEventTypes } = useGetAdverseEventTypes();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const buildingId = user?.buildingId;
  const lifeguardId = user?.id;
  const { swimmerId } = useParams<{ swimmerId?: string }>();
  const hasSwimmerId = !!swimmerId;
  const {
    data: swimmer,
    isLoading: isLoadingSwimmer,
    isError: isErrorSwimmer,
  } = useGetSwimmerById(swimmerId || "");

  const { mutate: createMaintenanceNew, isPending: isCreatingMaintenanceNew } =
    useCreateMaintenanceNew();
  const {
    mutate: createFirstAidStationNew,
    isPending: isCreatingFirstAidStationNew,
  } = useCreateFirstAidStationNew();
  const {
    mutate: createPoolRegulationNew,
    isPending: isCreatingPoolRegulationNew,
  } = useCreatePoolRegulationNew();
  const {
    mutate: createAdverseEventNew,
    isPending: isCreatingAdverseEventNew,
  } = useCreateAdverseEventNew();

  const navigate = useNavigate();

  const handleNavigateToDashboard = () => {
    navigate(`${ROUTES.DASHBOARD.LIFEGUARD}?tab=novedades`);
  };

  const updateNewsListCache = (createdNew: New) => {
    const listParams = getDefaultNewsListParams({
      building_id: buildingId,
      lifeguard_id: lifeguardId,
    });
    queryClient.setQueryData<PaginatedResponse<New>>(
      newQueryKeys.list(listParams),
      (old) => {
        if (!old) return old;
        const newItems = [createdNew, ...old.items].slice(0, old.page_size);
        const newTotal = old.total + 1;
        const newTotalPages = Math.ceil(newTotal / old.page_size);
        return {
          ...old,
          items: newItems,
          total: newTotal,
          total_pages: newTotalPages,
        };
      }
    );
  };

  const handleCreateMaintenanceNew = (formData: MaintenanceNewFormData) => {
    // event_timestamp is already in UTC ISO format from the form submission
    // security_officer_name is validated as required for maintenance reports
    createMaintenanceNew(
      {
        event_timestamp: formData.event_timestamp,
        report_type_id: parseInt(formData.report_type_id, 10),
        event_description: formData.event_description,
        security_officer_name: formData.security_officer_name!,
        maintenance_type_id: parseInt(formData.maintenance_type_id, 10),
      },
      {
        onSuccess: (createdNew) => {
          updateNewsListCache(createdNew);
          toast.success("Novedad de mantenimiento creada correctamente!");
          handleNavigateToDashboard();
        },
      }
    );
  };

  const handleCreateFirstAidStationNew = (formData: NewFormData) => {
    // event_timestamp is already in UTC ISO format from the form submission
    // security_officer_name is not sent for first aid station news
    createFirstAidStationNew(
      {
        event_timestamp: formData.event_timestamp,
        report_type_id: parseInt(formData.report_type_id, 10),
        event_description: formData.event_description,
        // swimmer_id is not allowed for first aid station news (backend validation)
        // security_officer_name is not required for first aid station news
      },
      {
        onSuccess: (createdNew) => {
          updateNewsListCache(createdNew);
          toast.success(
            "Novedad de Puesto de P.A / Botiquín creada correctamente!"
          );
          handleNavigateToDashboard();
        },
      }
    );
  };

  const handleCreatePoolRegulationNew = (
    formData: PoolRegulationNewFormData
  ) => {
    // event_timestamp is already in UTC ISO format from the form submission
    // swimmer_id is required for pool regulation news - use swimmerId from URL params if available, otherwise from form
    const swimmerIdToUse = swimmerId || swimmer?.id;

    // Check if intervention level is "Llamado de Atención 1" or "Llamado de Atención 2"
    // These intervention levels do NOT require security_officer_name (similar to First Aid Station)
    const isLlamadoDeAtencion = isLlamadoDeAtencionInterventionLevel(
      formData.intervention_level_id,
      interventionLevels
    );

    // Build the request payload conditionally
    const requestPayload: any = {
      event_timestamp: formData.event_timestamp,
      report_type_id: parseInt(formData.report_type_id, 10),
      event_description: formData.event_description,
      pool_regulation_id: parseInt(formData.pool_regulation_id, 10),
      intervention_level_id: parseInt(formData.intervention_level_id, 10),
      swimmer_id: swimmerIdToUse || "",
    };

    // Only include security_officer_name if NOT "Llamado de Atención 1/2"
    if (!isLlamadoDeAtencion) {
      requestPayload.security_officer_name = formData.security_officer_name!;
    }

    createPoolRegulationNew(requestPayload, {
      onSuccess: (createdNew) => {
        updateNewsListCache(createdNew);
        toast.success(
          "Novedad de incumplimiento de reglamento de piscina creada correctamente!"
        );
        handleNavigateToDashboard();
      },
    });
  };

  const handleCreateAdverseEventNew = (formData: AdverseEventNewFormData) => {
    // event_timestamp is already in UTC ISO format from the form submission
    // swimmer_id handling:
    // - For "Condiciones Meteorológicas" adverse event type: swimmer_id must be null/undefined
    // - For all other adverse event types: swimmer_id is required
    // Use swimmerId from URL params if available, otherwise from swimmer object
    // Note: Backend will validate swimmer_id based on adverse_events_type_id
    const swimmerIdToUse = swimmerId || swimmer?.id || undefined;

    createAdverseEventNew(
      {
        event_timestamp: formData.event_timestamp,
        report_type_id: parseInt(formData.report_type_id, 10),
        event_description: formData.event_description,
        security_officer_name: formData.security_officer_name!,
        adverse_events_type_id: parseInt(formData.adverse_events_type_id, 10),
        swimmer_id: swimmerIdToUse,
        spent_items: formData.spent_items || null,
        // Absent for Agresión Verbal/Física and Condiciones Meteorológicas
        event_specific_factor_id: formData.event_specific_factor_id
          ? parseInt(formData.event_specific_factor_id, 10)
          : undefined,
        adverse_event_intervention_level_id:
          formData.adverse_event_intervention_level_id
            ? parseInt(formData.adverse_event_intervention_level_id, 10)
            : undefined,
      },
      {
        onSuccess: (createdNew) => {
          updateNewsListCache(createdNew);
          toast.success("Novedad de evento adverso creada correctamente!");
          handleNavigateToDashboard();
        },
      }
    );
  };

  const handleCreateNew = (formData: NewFormData) => {
    const reportTypeId = parseInt(formData.report_type_id, 10);

    // This should not happen because maintenance forms are handled separately
    // but we keep this as a fallback
    if (isMaintenanceReportType(reportTypeId, reportTypes)) {
      toast.error(
        "Los reportes de mantenimiento requieren campos adicionales. Por favor complete el formulario de mantenimiento."
      );
      return;
    }

    // This should not happen because first aid station forms are handled separately
    // but we keep this as a fallback
    if (isFirstAidStationReportType(reportTypeId, reportTypes)) {
      toast.error(
        "Los reportes de Puesto de P.A / Botiquín deben ser procesados correctamente. Por favor intente nuevamente."
      );
      return;
    }

    // This should not happen because pool regulation forms are handled separately
    // but we keep this as a fallback
    if (isPoolRegulationReportType(reportTypeId, reportTypes)) {
      toast.error(
        "Los reportes de incumplimiento de reglamento de piscina requieren campos adicionales. Por favor complete el formulario correspondiente."
      );
      return;
    }

    // This should not happen because adverse event forms are handled separately
    // but we keep this as a fallback
    if (isAdverseEventReportType(reportTypeId, reportTypes)) {
      toast.error(
        "Los reportes de eventos adversos requieren campos adicionales. Por favor complete el formulario correspondiente."
      );
      return;
    }

    // Generic message for other report types that are not yet implemented
    toast.error(
      "Este tipo de reporte requiere campos adicionales específicos. Por favor use el formulario correspondiente a su tipo de reporte."
    );
  };

  const handleFormSubmit = (
    data:
      | NewFormData
      | MaintenanceNewFormData
      | PoolRegulationNewFormData
      | AdverseEventNewFormData
  ) => {
    // Note: Adverse event type transformations are handled in useRegisterNewForm
    // Data arrives here already transformed (adverse_X → proper report_type_id + adverse_events_type_id)
    
    const reportTypeId = parseInt(data.report_type_id, 10);

    // Route to the appropriate handler based on report type
    if (isMaintenanceReportType(reportTypeId, reportTypes)) {
      handleCreateMaintenanceNew(data as MaintenanceNewFormData);
    } else if (isFirstAidStationReportType(reportTypeId, reportTypes)) {
      handleCreateFirstAidStationNew(data as NewFormData);
    } else if (isPoolRegulationReportType(reportTypeId, reportTypes)) {
      handleCreatePoolRegulationNew(data as PoolRegulationNewFormData);
    } else if (isAdverseEventReportType(reportTypeId, reportTypes)) {
      handleCreateAdverseEventNew(data as AdverseEventNewFormData);
    } else {
      // Other report types (to be implemented)
      handleCreateNew(data as NewFormData);
    }
  };

  return {
    reportTypes: reportTypes ?? [],
    maintenanceTypes: maintenanceTypes ?? [],
    poolRegulationTypes: poolRegulationTypes ?? [],
    interventionLevels: interventionLevels ?? [],
    adverseEventTypes: adverseEventTypes ?? [],
    handleFormSubmit,
    handleNavigateToDashboard,
    isSubmitting:
      isCreatingMaintenanceNew ||
      isCreatingFirstAidStationNew ||
      isCreatingPoolRegulationNew ||
      isCreatingAdverseEventNew,
    // Only show loading/error states if we're actually trying to load a swimmer
    isLoadingSwimmer: hasSwimmerId && isLoadingSwimmer,
    isErrorSwimmer: hasSwimmerId && isErrorSwimmer,
    swimmer,
  };
};
